import { ActionIcon, CloseButton, Group, Loader, Menu, SegmentedControl, Select, Text, TextInput, Title } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconArrowDown, IconArrowUp, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { FloatingActionButton } from "shared/components/global/FloatingActionButton/FloatingActionButton";
import { MessageListSkeleton } from "shared/components/message/MessageListSkeleton";
import { TemplateSearchRepository } from "shared/components/message/templates/data";
import { TemplateCard } from "shared/components/message/templates/TemplateCard";
import { usePermissionCheck } from "shared/stores/oidc";
import { NoMessagesFigure } from "src/assets/NoMessagesFigure";
import { graphql } from "src/gql";
import { MessageSeverity, Permission, SortOrder, TemplatesSortOptions } from "src/gql/graphql";
import { match } from "ts-pattern";
import { container } from "tsyringe";

const Page = () => {
    const canSendToGroup = usePermissionCheck(Permission.GroupMessageSendTo);
    const canSendToTopic = usePermissionCheck(Permission.TopicMessageSendTo);
    const canSend = canSendToGroup || canSendToTopic;
    const canCreateTemplate = usePermissionCheck(Permission.GroupTemplateCreate);

    const [query, setQuery] = useState("");
    const [debouncedQuery] = useDebouncedValue(query, 300);
    const [severity, setSeverity] = useState("All");
    const [sortBy, setSortBy] = useState(TemplatesSortOptions.LastModified);
    const [sortDirection, setSortDirection] = useState(SortOrder.Desc);

    const [groupSearch, setGroupSearch] = useState("");
    const [groupSearchDebounced] = useDebouncedValue(groupSearch, 300);
    const [groupFilter, setGroupFilter] = useState<string>();

    const search = {
        query: debouncedQuery.length > 1 ? debouncedQuery : undefined,
        groupId: groupFilter,
        severity: match(severity)
            .with("Urgent", () => MessageSeverity.Urgent)
            .with("Informational", () => MessageSeverity.Informational)
            .otherwise(() => undefined),
        sort: { by: sortBy, direction: sortDirection },
    };

    const groupFilterSearchResults = useQuery({
        queryFn: () => httpPostGraphql(gql, { search: groupSearchDebounced }),
        queryKey: ["templates-filter-group-search", groupSearchDebounced],
    });

    const loader = container.resolve(TemplateSearchRepository).get(
        { search },
        {
            sort: {
                by: (t) =>
                    match(sortBy)
                        .with(TemplatesSortOptions.LastModified, () => t.updatedAt)
                        .with(TemplatesSortOptions.Created, () => t.createdAt)
                        .with(TemplatesSortOptions.Subject, () => t.message.subject.toLowerCase())
                        .exhaustive(),
                ord: sortDirection == SortOrder.Asc ? "asc" : "desc",
            },
        },
    );
    const templates = loader.useItems();
    const { isLoading } = loader.useStatus();

    return (
        <Content>
            <Content.Heading w="100%">
                <Group w="100%" justify="space-between">
                    <Title order={3} size="h1">
                        Templates
                    </Title>
                    <SegmentedControl data={["All", "Urgent", "Informational"]} onChange={(sev) => setSeverity(sev)} />
                </Group>
            </Content.Heading>
            <Group justify="space-between" w="100%">
                <Group>
                    <TextInput
                        leftSection={<IconSearch size={16} />}
                        placeholder="Search templates"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        rightSection={
                            query.length != 0 && <CloseButton size="sm" onClick={() => setQuery("")} aria-label="Clear search value" />
                        }
                        rightSectionPointerEvents={query.length == 0 ? "none" : "all"}
                    />
                    <Select
                        placeholder="Group"
                        searchable
                        clearable
                        searchValue={groupSearch}
                        onSearchChange={setGroupSearch}
                        onChange={(id, _) => setGroupFilter(id ?? undefined)}
                        data={groupFilterSearchResults.data?.searchGroups.map((it) => ({ label: it.name, value: it.id }))}
                    />
                </Group>
                <Group gap="xs">
                    <ActionIcon
                        variant="default"
                        size="input-sm"
                        onClick={() => setSortDirection(sortDirection == SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc)}
                    >
                        {sortDirection == SortOrder.Asc ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
                    </ActionIcon>
                    <Select
                        allowDeselect={false}
                        defaultValue={TemplatesSortOptions.LastModified}
                        data={[
                            { label: "Last Modified", value: TemplatesSortOptions.LastModified },
                            { label: "Created", value: TemplatesSortOptions.Created },
                            { label: "Subject", value: TemplatesSortOptions.Subject },
                        ]}
                        onChange={(sort) => setSortBy(sort as TemplatesSortOptions)}
                    />
                </Group>
            </Group>

            {(canSend || canCreateTemplate) && (
                <FloatingActionButton>
                    {canSend && (
                        <Menu.Item component={Link} to="/messages/compose">
                            Create Message
                        </Menu.Item>
                    )}
                    {canCreateTemplate && (
                        <Menu.Item component={Link} to="/messages/templates/create">
                            Create Template
                        </Menu.Item>
                    )}
                </FloatingActionButton>
            )}

            <Suspense fallback={<Loader />}>
                {templates.map((template) => (
                    <TemplateCard key={template.id} template={template} />
                ))}
            </Suspense>

            {templates.length == 0 &&
                (isLoading ? (
                    <MessageListSkeleton />
                ) : (
                    <NoMessagesFigure>
                        <Text ta={"center"}>Looks like you don't have any Templates yet</Text>
                    </NoMessagesFigure>
                ))}

            <loader.LoadTrigger />
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/messages/templates/")({
    component: Page,
});

const gql = graphql(`
    query TemplatesFilterGroupSearch($search: String!) {
        searchGroups(limit: 10, offset: 0, permissions: [], query: $search) {
            ...FullGroupLike
        }
    }
`);
